import { z } from "zod";

import type { AssessmentLayoutConfig } from "@components/AssessmentFormRenderer/types/layoutTypes";

// Split out of DocumentEditor.tsx so this parsing logic is unit-testable without
// mounting the component, and so DocumentEditor.tsx exports only the component
// (react-refresh/only-export-components requires component files to have no other exports).
type ParsedDataType = Record<string, unknown>;

/**
 * Pipeline results are stored wrapped in a key like "<FILE_KIND>_assessment_1_results"
 * (see backend LearnerFileService._unwrap_parsed_result in api-django). Detect and strip
 * that wrapper deterministically by key name, matching the backend's own convention,
 * rather than guessing which schema shape happens to validate.
 */
export function unwrapParsedResult(parsed: unknown, fileKind: string): { wrapperKey: string | null; inner: unknown } {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { wrapperKey: null, inner: parsed };
  }

  const prefix = `${fileKind}_assessment_`.toLowerCase();
  for (const [key, inner] of Object.entries(parsed as Record<string, unknown>)) {
    const lowerKey = key.toLowerCase();
    if (!lowerKey.startsWith(prefix) || !lowerKey.endsWith("_results")) continue;
    if (!inner || typeof inner !== "object" || Array.isArray(inner)) continue;

    return { wrapperKey: key, inner };
  }

  return { wrapperKey: null, inner: parsed };
}

interface ParsedFormState {
  parsedData: ParsedDataType | null;
  wrapperKey: string | null;
  values: ParsedDataType | null;
  errors: z.core.$ZodIssue[];
}

/**
 * Parses the raw parsed-result string against the assessment's Zod schema,
 * unwrapping the pipeline's wrapper key first.
 */
export function parseFormState(
  parsedResultString: string | null | undefined,
  config: AssessmentLayoutConfig | undefined,
  fileKind: string,
): ParsedFormState {
  if (!parsedResultString || !config) {
    return { parsedData: null, wrapperKey: null, values: null, errors: [] };
  }

  const parsed = JSON.parse(parsedResultString);
  const { parsedResultSchema } = config;

  const { wrapperKey: wKey, inner } = unwrapParsedResult(parsed, fileKind);
  const parseResult = parsedResultSchema.safeParse(inner);

  return {
    parsedData: parsed,
    wrapperKey: wKey,
    values: parseResult.success ? parseResult.data : null,
    errors: parseResult.success ? [] : parseResult.error?.issues ?? [],
  };
}
